import { groupBy } from "lodash-es";
import type React from "react";
import { twJoin } from "tailwind-merge";

import type { ConstraintListItemDto } from "@/api/types";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";
import { Popover } from "@/ui-adapters/ArkPopover";

export interface TagListProps {
  tags: TagListItem[];
  limit: number;
}
export function TagList({ tags: allTags, limit }: TagListProps): React.ReactNode {
  const { t } = useTranslation();
  const [isOpen, openHandlers] = useBool();
  const tagsToShow = allTags.slice(0, limit);
  const tagsToHide = allTags.slice(limit);

  return (
    <Inline gap="xs" align="center" data-testid="tag-list-root">
      <TagListRow tags={tagsToShow} />
      {tagsToHide.length > 0 && (
        <span className="ms-space-sm w-fit">
          <Button
            variant="ghostPrimary"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isOpen) {
                openHandlers.setFalse();
              } else {
                openHandlers.setTrue();
              }
            }}
          >
            <span className="text-caption-emphasis">
              {t("common.label.x-more", {
                count: tagsToHide.length,
              })}
            </span>
          </Button>
        </span>
      )}

      <Popover.Root
        open={isOpen}
        onInteractOutside={openHandlers.setFalse}
        positioning={{
          placement: "right-start",
          offset: {
            crossAxis: -15,
          },
        }}
      >
        <Popover.Anchor />
        <Popover.Positioner className="z-50!">
          <Popover.Content
            className={twJoin(
              "max-w-tooltip-fluid text-caption whitespace-pre-line @md:max-w-sm",
              "data-[placement=left-end]:origin-bottom-right data-[placement=left-start]:origin-top-right data-[placement=right-end]:origin-bottom-left data-[placement=right-start]:origin-top-left",
              "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out",
            )}
          >
            <TagListTooltip tags={allTags} />
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Inline>
  );
}

interface TagListTooltipProps {
  tags: TagListItem[];
}
function TagListTooltip({ tags }: TagListTooltipProps) {
  const { t } = useTranslation();
  const tagsByType = groupBy(tags, ({ type }) => type);
  function getTypeTranslation(type: string) {
    switch (type) {
      case "floor":
        return t("model.constraint.type.floor");
      case "building":
        return t("model.constraint.type.building");
      case "apartmentType":
        return t("model.constraint.type.apartment-type");
      default:
        return type;
    }
  }

  return (
    <Box padding="sm" radius="sm" border="secondary" background="default" elevation="raised">
      <Stack gap="sm">
        {Object.entries(tagsByType).map(([type, tags]) => {
          return (
            <Stack key={type} gap="xs">
              <span className="text-caption-emphasis">{getTypeTranslation(type)}:</span>
              <TagListRow tags={tags} />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

interface TagListRowProps {
  tags: TagListItem[];
}
function TagListRow({ tags }: TagListRowProps) {
  return (
    <Inline as="ul" gap="xs">
      {tags.map(({ id, content }) => (
        <li key={id}>
          <Badge variant="white">{content}</Badge>
        </li>
      ))}
    </Inline>
  );
}

interface TagListItem {
  id: string;
  content: React.ReactNode;
  type: string;
}

export function transformConstraintToTag({ key, ...constraint }: ConstraintListItemDto): TagListItem {
  return { content: key, ...constraint };
}
